import type { BuildingInStoreInfo, FloorInfo, LocalizedString, PoiContent } from '@/services/dto';
import { getFloorMapName } from '@/utils/floor-guide';

type StoreFloorNameEntry = {
    code?: string;
    lang?: string;
    name?: string;
    text?: string;
};

const normalizeStoreFloorNameField = (name: LocalizedString | StoreFloorNameEntry[] | unknown): LocalizedString => {
    if (!Array.isArray(name)) {
        return (name as LocalizedString) ?? { ko: '', en: '' };
    }

    const result: LocalizedString = { ko: '', en: '' };
    for (const item of name) {
        const code = item.code ?? item.lang;
        const text = item.name ?? item.text ?? '';
        if (code) {
            result[code] = text;
        }
    }
    return result;
};

/**
 * Tên tầng (ko) khi đọc floor từ store API — hỗ trợ `name` chưa normalize (mảng `{ code, name }`).
 * Dùng riêng cho lọc category menu FLOOR; không thay `getFloorMapName`.
 */
export const getStoreFloorMapNameKo = (floor: Pick<FloorInfo, 'name'>): string => {
    if (Array.isArray(floor.name)) {
        return normalizeStoreFloorNameField(floor.name).ko || '';
    }
    return getFloorMapName(floor);
};

/**
 * Tập tên tầng (ko) của tòa đặt kiosk — dùng để giới hạn POI trên map nhiều tòa:
 * tòa kiosk giữ full danh sách tầng; tòa khác chỉ tính POI có tên tầng trùng tập này.
 */
export const getKioskBuildingAllowedFloorMapNames = (
    storeBuildings: BuildingInStoreInfo[] | undefined,
    kioskBuildingId: string | undefined
): Set<string> | null => {
    if (!kioskBuildingId || !storeBuildings?.length) {
        return null;
    }

    const kioskBuilding = storeBuildings.find((building) => building.id === kioskBuildingId);
    if (!kioskBuilding?.floors?.length) {
        return null;
    }

    const names = kioskBuilding?.floors?.map((floor) => getStoreFloorMapNameKo(floor)).filter(Boolean);

    return names.length ? new Set(names) : null;
};

export const resolvePoiFloorMapName = (poi: PoiContent, storeBuildings: BuildingInStoreInfo[]): string => {
    const building = storeBuildings.find((item) => item.studioBuildingId === poi.studioBuildingId);
    if (!building?.floors?.length) {
        return '';
    }

    const floor = building.floors.find((item) => item.floor_code === poi.studioFloorId);
    return floor ? getStoreFloorMapNameKo(floor) : '';
};

export const isPoiWithinKioskFloorScope = (
    poi: PoiContent,
    allowedFloorMapNames: Set<string> | null,
    storeBuildings: BuildingInStoreInfo[] | undefined
): boolean => {
    if (!allowedFloorMapNames || !storeBuildings?.length) {
        return true;
    }

    const floorMapName = resolvePoiFloorMapName(poi, storeBuildings);
    if (!floorMapName) {
        return false;
    }

    return allowedFloorMapNames.has(floorMapName);
};
